import { PriorityHigh as PriorityHighIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ItemInfrastructureDto,
  infrastructureType,
  infrastructureTypes,
  stringifyPowerType,
} from '@nohara-dxdev/mt-common';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemCircuitEntity } from '@/types/api';

interface ChildItemInfo {
  id: string;
  name: string;
  itemNumber: string;
  infrastructureRelationshipConfigs?: any[];
}

interface ItemInfrastructureFormPreviewProps {
  infrastructures: ItemInfrastructureDto[];
  circuits: ItemCircuitEntity[];
  getChildItemInfo?: (childItemId: string) => ChildItemInfo | undefined;
}

/**
 * Preview-only component for displaying item infrastructures
 * No data fetching, no editing, just pure UI display
 */
export const ItemInfrastructureFormPreview = ({
  infrastructures,
  circuits,
  getChildItemInfo,
}: ItemInfrastructureFormPreviewProps) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const currentInfrastructureType = infrastructureTypes[selectedTab];

  // Get items for current tab
  const currentTabItems = useMemo(() => {
    return infrastructures.filter(
      (infra) => infra.infrastructureType === currentInfrastructureType,
    );
  }, [currentInfrastructureType, infrastructures]);

  // Build circuit map from circuits
  const circuitMap = useMemo(() => {
    return circuits.reduce<Record<string, (typeof circuits)[number] & { formatted: string }>>(
      (acc, circuit) => {
        if (circuit.id) {
          acc[circuit.id] = {
            ...circuit,
            formatted: stringifyPowerType(circuit),
          };
        }
        return acc;
      },
      {},
    );
  }, [circuits]);

  if (infrastructureTypes.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="textSecondary">
          {t('itemTemplates.detail.noInfrastructureTypes')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Infrastructure Type Tabs */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => {
            setSelectedTab(newValue);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTabs-flexContainer': {
              gap: 1,
            },
            '& .MuiTab-root': {
              minHeight: 40,
              py: 1,
              px: 2,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            },
          }}
        >
          {infrastructureTypes.map((infrastructureType) => (
            <Tab key={infrastructureType} label={t(`infrastructure.${infrastructureType}`)} />
          ))}
        </Tabs>
      </Box>

      {/* Items Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('items.list.columns.itemNumber')}</TableCell>
              <TableCell>{t('items.list.columns.name')}</TableCell>
              {currentInfrastructureType === infrastructureType.electricity && (
                <>
                  <TableCell>{t('itemTemplates.detail.childCircuit')}</TableCell>
                  <TableCell>{t('itemTemplates.detail.typeOfPower')}</TableCell>
                </>
              )}
              <TableCell>{t('itemTemplates.detail.load')}</TableCell>
              <TableCell>{t('itemTemplates.detail.unit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentTabItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {t('itemTemplates.detail.noInfrastructureItems')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              currentTabItems.map((infrastructure) => {
                const item = getChildItemInfo?.(infrastructure.childItemId);

                return (
                  <TableRow key={infrastructure.childItemId}>
                    <TableCell>{item?.itemNumber ?? '-'}</TableCell>
                    <TableCell>{item?.name ?? '-'}</TableCell>
                    {infrastructure.infrastructureType === infrastructureType.electricity && (
                      <>
                        <TableCell>
                          {(infrastructure.circuitId &&
                            circuitMap[infrastructure.circuitId].name) ?? (
                            <PriorityHighIcon color="error" />
                          )}
                        </TableCell>
                        <TableCell>
                          {(infrastructure.circuitId &&
                            circuitMap[infrastructure.circuitId].formatted) ?? (
                            <PriorityHighIcon color="error" />
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      {(() => {
                        const config = item?.infrastructureRelationshipConfigs?.find(
                          (c: any) => c.infrastructureType === currentInfrastructureType,
                        );
                        return config?.load ?? '-';
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const config = item?.infrastructureRelationshipConfigs?.find(
                          (c: any) => c.infrastructureType === currentInfrastructureType,
                        );
                        return config?.unit ?? '-';
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
